import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { RootState } from '@/store/store';
import {
    AccountDetail,
    Authority,
    ParticipantAuthority,
    ParticipantAuthorityPayload,
} from '@/store/modules/account/account.types';
import {
    canExportScreening,
    canMutateScreening,
    canScreenRecords,
    getAccountProjectRoles,
    getParticipantAuthorities,
    hasAuthority,
} from '@/utils/general.util';

/**
 * SUMDEV-3492 — shared role checks for Screening UI.
 * UPO may export; PO / Privileged Reviewer may mutate;
 * PO / PR / Reviewer may screen (T&A / FT include-exclude / notes).
 */
@Component({})
export default class ScreeningRoleMixin extends Vue {
    @Action('account/getParticipantAuthority')
    getParticipantAuthority!: (payload: ParticipantAuthorityPayload) => void;

    @State((state: RootState) => state.account.getParticipantAuthority.data)
    participantAuthority!: ParticipantAuthority;

    @State((state: RootState) => state.account.getAccount.data)
    screeningRoleAccountDetail!: AccountDetail;

    get participantAuthorities(): Authority[] {
        const fromParticipant = getParticipantAuthorities(
            this.participantAuthority?.participantAuthorities
        );

        // Merge account.projectRoles so UPO is detected even if participant
        // authority is stale/missing after license expiry demotion.
        const projectId = this.$route?.params?.projectId;
        const fromAccount = getAccountProjectRoles(
            this.screeningRoleAccountDetail?.projectRoles,
            projectId
        ) as Authority[];

        return [...fromParticipant, ...fromAccount];
    }

    get canExportScreening(): boolean {
        return canExportScreening(this.participantAuthorities);
    }

    get canMutateScreening(): boolean {
        return canMutateScreening(this.participantAuthorities);
    }

    get canScreenRecords(): boolean {
        return canScreenRecords(this.participantAuthorities);
    }

    get isUnlicencedProjectOwner(): boolean {
        return hasAuthority(
            this.participantAuthorities,
            Authority.ROLE_UNLICENCED_PROJECT_OWNER
        );
    }

    mounted(): void {
        this.refreshParticipantAuthority();
    }

    @Watch('screeningRoleAccountDetail')
    onScreeningRoleAccountDetailLoaded(): void {
        this.refreshParticipantAuthority();
    }

    @Watch('$route.params.projectId')
    onProjectIdChanged(): void {
        this.refreshParticipantAuthority();
    }

    /** Always refresh — PO→UPO can change after setting license expiry. */
    private refreshParticipantAuthority(): void {
        const projectId = +this.$route.params.projectId;
        const userId = +this.screeningRoleAccountDetail?.id;
        if (projectId && userId && !isNaN(userId)) {
            this.getParticipantAuthority({ projectId, userId });
        }
    }
}
